<script setup>
import { computed, ref, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { OnClickOutside } from '@vueuse/components';

import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import AssistantForm from 'dashboard/components-next/pilot/pageComponents/assistant/AssistantForm.vue';

const props = defineProps({
  assistant: {
    type: Object,
    default: () => ({}),
  },
  isFetching: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([]);

const { t } = useI18n();
const router = useRouter();
const store = useStore();

const status = computed(() => props.assistant?.status || 'draft');
const isPublished = computed(() => status.value === 'published');
const hasAvatar = computed(() => !!props.assistant?.avatar_url);

const statusLabel = computed(() =>
  isPublished.value
    ? t('PILOT.ASSISTANTS.CONFIG.STATUS.PUBLISHED')
    : t('PILOT.ASSISTANTS.CONFIG.STATUS.DRAFT')
);

const statusClass = computed(() =>
  isPublished.value
    ? 'bg-n-blue-1 dark:bg-n-blue-2 text-n-blue-10 dark:text-n-blue-9'
    : 'bg-n-amber-1 dark:bg-n-amber-2 text-n-amber-10 dark:text-n-amber-9'
);

const statusDotClass = computed(() =>
  isPublished.value ? 'bg-n-blue-5' : 'bg-n-amber-5'
);

// 下拉菜单状态
const showMoreMenu = ref(false);
const moreMenuItems = computed(() => {
  const items = [];

  // 根据状态添加发布/下架选项
  if (!isPublished.value) {
    items.push({
      label: t('PILOT.ASSISTANTS.CONFIG.PUBLISH'),
      action: 'publish',
      value: 'publish',
      icon: 'i-lucide-upload-cloud',
    });
  } else {
    items.push({
      label: t('PILOT.ASSISTANTS.CONFIG.UNPUBLISH'),
      action: 'unpublish',
      value: 'unpublish',
      icon: 'i-lucide-download-cloud',
    });
  }

  items.push({
    label: t('PILOT.ASSISTANTS.CONFIG.DELETE'),
    action: 'delete',
    value: 'delete',
    icon: 'i-lucide-trash-2',
  });

  return items;
});

// 编辑弹窗状态
const isEditPopoverOpen = ref(false);
const editButtonRef = ref(null);
const assistantFormRef = ref(null);

// 头像相关函数
const getInitial = name => {
  if (!name) return 'A';
  return name.charAt(0).toUpperCase();
};

const getColor = name => {
  const colors = [
    '#8B5CF6',
    '#3B82F6',
    '#10B981',
    '#F59E0B',
    '#EF4444',
    '#EC4899',
    '#6366F1',
  ];
  const index = (name?.charCodeAt(0) || 0) % colors.length;
  return colors[index];
};

// 编辑弹窗位置
const getEditPopoverStyle = () => {
  if (!editButtonRef.value) return {};
  const buttonRect = editButtonRef.value.getBoundingClientRect();
  return {
    top: `${buttonRect.bottom + 8}px`,
    left: `${buttonRect.left}px`,
    minWidth: '400px',
  };
};

const openEditPopover = () => {
  isEditPopoverOpen.value = true;
};

const closeEditPopover = () => {
  isEditPopoverOpen.value = false;
};

const handleEditSubmit = async formData => {
  try {
    await store.dispatch('pilotAssistants/update', {
      id: props.assistant.id,
      data: formData,
    });
    // 重新获取助手数据以更新显示
    await store.dispatch('pilotAssistants/show', { id: props.assistant.id });
    useAlert(t('PILOT.ASSISTANTS.EDIT.SUCCESS_MESSAGE'));
    closeEditPopover();
  } catch (error) {
    const errorMessage =
      error?.message || t('PILOT.ASSISTANTS.EDIT.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

const handleBack = () => {
  router.push({
    name: 'pilot_assistants_create_index',
    params: { accountId: router.currentRoute.value.params.accountId },
  });
};

const handlePublish = async () => {
  try {
    await store.dispatch('pilotAssistants/update', {
      id: props.assistant.id,
      data: { status: 'published' },
    });
    useAlert(t('PILOT.ASSISTANTS.CONFIG.PUBLISH_SUCCESS'));
  } catch (error) {
    useAlert(t('PILOT.ASSISTANTS.CONFIG.PUBLISH_ERROR'));
  }
};

const handleUnpublish = async () => {
  try {
    await store.dispatch('pilotAssistants/update', {
      id: props.assistant.id,
      data: { status: 'draft' },
    });
    useAlert(t('PILOT.ASSISTANTS.CONFIG.UNPUBLISH_SUCCESS'));
  } catch (error) {
    useAlert(t('PILOT.ASSISTANTS.CONFIG.UNPUBLISH_ERROR'));
  }
};

const handleDelete = async () => {
  try {
    await store.dispatch('pilotAssistants/destroy', { id: props.assistant.id });
    useAlert(t('PILOT.ASSISTANTS.CONFIG.DELETE_SUCCESS'));
    handleBack();
  } catch (error) {
    useAlert(t('PILOT.ASSISTANTS.CONFIG.DELETE_ERROR'));
  }
};

const handleMenuAction = ({ action }) => {
  showMoreMenu.value = false;
  if (action === 'delete') {
    handleDelete();
  } else if (action === 'publish') {
    handlePublish();
  } else if (action === 'unpublish') {
    handleUnpublish();
  }
};
</script>

<template>
  <div
    class="flex items-center justify-between px-6 py-4 bg-n-slate-white dark:bg-n-slate-900 border-b border-n-weak"
  >
    <!-- 左侧：返回 + 助手信息 -->
    <div class="flex items-center gap-3">
      <button
        type="button"
        class="p-2 -ml-2 text-n-slate-600 dark:text-n-slate-400 hover:bg-n-slate-100 dark:hover:bg-n-slate-700 rounded-lg transition-colors"
        @click="handleBack"
      >
        <Icon icon="i-lucide-arrow-left" class="h-5 w-5" />
      </button>

      <div v-if="isFetching" class="flex items-center gap-3">
        <Spinner size="small" />
        <span class="text-n-slate-11">{{
          t('PILOT.ASSISTANTS.CONFIG.LOADING')
        }}</span>
      </div>

      <template v-else>
        <div class="flex items-center gap-3">
          <!-- 头像：与助手卡片逻辑一致 -->
          <img
            v-if="hasAvatar"
            :src="assistant.avatar_url"
            :alt="assistant.name"
            class="w-10 h-10 rounded-lg object-cover shadow-sm"
          />
          <div
            v-else
            class="w-10 h-10 rounded-lg flex items-center justify-center shadow-sm"
            :style="{
              backgroundColor: getColor(assistant?.name || 'Assistant'),
            }"
          >
            <span class="text-lg font-semibold text-white">
              {{ getInitial(assistant?.name) }}
            </span>
          </div>
          <div class="flex items-center gap-2">
            <h1 class="text-base font-medium text-n-slate-12">
              {{ assistant?.name || t('PILOT.ASSISTANTS.CONFIG.UNTITLED') }}
            </h1>
            <!-- 编辑按钮 -->
            <div ref="editButtonRef">
              <Button
                icon="i-lucide-pencil-line"
                size="sm"
                ghost
                @click="openEditPopover"
              />
            </div>
            <!-- 状态标签 -->
            <div
              class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
              :class="statusClass"
            >
              <span class="w-1.5 h-1.5 rounded-full" :class="statusDotClass" />
              <span>{{ statusLabel }}</span>
            </div>
          </div>
        </div>
      </template>
    </div>

    <!-- 右侧：操作按钮 -->
    <div class="flex items-center gap-2 relative">
      <Button
        icon="i-lucide-more-horizontal"
        size="sm"
        ghost
        @click="showMoreMenu = !showMoreMenu"
      />
      <OnClickOutside @trigger="showMoreMenu = false">
        <DropdownMenu
          v-if="showMoreMenu"
          :menu-items="moreMenuItems"
          class="absolute right-0 top-full mt-2"
          @action="handleMenuAction"
        />
      </OnClickOutside>
    </div>
  </div>

  <!-- 编辑弹窗 -->
  <Teleport to="body">
    <transition
      name="fade"
      enter-active-class="transition-opacity duration-200"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-150"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="isEditPopoverOpen && assistant"
        class="fixed z-50"
        :style="getEditPopoverStyle()"
      >
        <OnClickOutside @trigger="closeEditPopover">
          <div
            class="bg-n-alpha-3 backdrop-blur-[100px] border border-n-container outline outline-1 outline-n-container rounded-xl shadow-xl overflow-hidden"
          >
            <div class="p-6">
              <AssistantForm
                ref="assistantFormRef"
                mode="edit"
                :assistant="assistant"
                @submit="handleEditSubmit"
                @cancel="closeEditPopover"
              />
            </div>
          </div>
        </OnClickOutside>
      </div>
    </transition>
  </Teleport>
</template>
